// Image compression for receipt processing
// Resizes large photos to max 1500px and compresses to JPEG 80%
// Typical result: 5MB phone photo → 200-400KB

const MAX_DIMENSION = 1500;
const JPEG_QUALITY = 0.80;

/**
 * Compress an image file for AI processing.
 * Returns { base64, mediaType } ready for the Claude API.
 * PDFs and small images pass through unchanged.
 */
export async function compressForAI(file) {
  const mediaType = getMediaType(file);

  // PDFs can't be compressed this way — pass through
  if (mediaType === 'application/pdf') {
    const base64 = await fileToBase64Raw(file);
    return { base64, mediaType };
  }

  // Small images (under 1MB) — pass through, no need to compress
  if (file.size < 1024 * 1024) {
    const base64 = await fileToBase64Raw(file);
    return { base64, mediaType };
  }

  // Large images — resize and compress via canvas
  console.log(`[Ground Control] Compressing ${Math.round(file.size / 1024)}KB image...`);
  const { base64, newSize } = await resizeImage(file);
  console.log(`[Ground Control] Compressed to ${Math.round(newSize / 1024)}KB`);

  return { base64, mediaType: 'image/jpeg' };
}

/**
 * Get the original file as base64 for Drive upload (no compression)
 */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function fileToBase64Raw(file) {
  return fileToBase64(file);
}

export function getMediaType(file) {
  const ext = file.name.split('.').pop().toLowerCase();
  const map = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    heic: 'image/heic',
    pdf: 'application/pdf',
  };
  return map[ext] || 'application/octet-stream';
}

function resizeImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;

      // Scale down to MAX_DIMENSION on the longest side
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        if (width > height) {
          height = Math.round(height * (MAX_DIMENSION / width));
          width = MAX_DIMENSION;
        } else {
          width = Math.round(width * (MAX_DIMENSION / height));
          height = MAX_DIMENSION;
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to JPEG base64
      const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
      const base64 = dataUrl.split(',')[1];

      // Estimate compressed size
      const newSize = Math.round(base64.length * 0.75); // base64 overhead

      resolve({ base64, newSize });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image for compression'));
    };

    img.src = url;
  });
}
